import {
  Barcode,
  BarcodeCapture,
  BarcodeCaptureListener,
  barcodeCaptureLoader,
  BarcodeCaptureOverlay,
  BarcodeCaptureSession,
  BarcodeCaptureSettings,
} from "@scandit/web-datacapture-barcode";
import {
  Camera,
  CameraSwitchControl,
  DataCaptureContext,
  DataCaptureView,
  FrameData,
  FrameSourceState,
  LaserlineViewfinder,
  Viewfinder,
} from "@scandit/web-datacapture-core";
import { scanditLicense } from "env";
import EventEmitter from "eventemitter3";
import { getSymbologies } from "./Symbologies";

export type BarcodeScannerEvents = {
  onScan: (barcode: string) => void;
};

class BarcodeScanner extends EventEmitter<BarcodeScannerEvents> implements BarcodeCaptureListener {
  #camera: Camera | null = null;
  #context: DataCaptureContext | null = null;
  #barcodeCapture: BarcodeCapture | null = null;
  #initialized: boolean = false;

  constructor() {
    super();
    this.didScan = this.didScan.bind(this);
  }

  async initialize() {
    if (this.#initialized) return;
    if (!scanditLicense()) return;
    this.#context = await DataCaptureContext.forLicenseKey(scanditLicense()!, {
      moduleLoaders: [barcodeCaptureLoader({ highEndBlurryRecognition: false })],
      libraryLocation:
        "https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.2.0/sdc-lib/",
    });

    // The barcode capturing process is configured through barcode capture settings,
    // they are then applied to the barcode capture instance that manages barcode recognition.
    const settings: BarcodeCaptureSettings = new BarcodeCaptureSettings();
    // The settings instance initially has all types of barcodes (symbologies) disabled. For the purpose of this
    // sample we enable a very generous set of symbologies. In your own app ensure that you only enable the
    // symbologies that your app requires as every additional enabled symbology has an impact on processing times.
    settings.enableSymbologies(getSymbologies());
    // Filter out duplicate barcodes for 1 second.
    settings.codeDuplicateFilter = -1;
    this.#barcodeCapture = await BarcodeCapture.forContext(this.#context, settings);
    // Disable the barcode capture mode until the camera is accessed.
    this.#barcodeCapture.setEnabled(false);

    // Try to use the world-facing (back) camera and set it as the frame source of the context. The camera is off by
    // default and must be turned on to start streaming frames to the data capture context for recognition.
    this.#camera = Camera.pickBestGuess();
    await this.#camera.applySettings(BarcodeCapture.recommendedCameraSettings);
    await this.#camera.setMirrorImageEnabled(false);

    this.#barcodeCapture.addListener(this);
    this.#initialized = true;
  }

  didScan(barcodeCapture: BarcodeCapture, session: BarcodeCaptureSession, frameData: FrameData) {
    const barcode: Barcode | null = session.newlyRecognizedBarcode;
    if (barcode && barcode.data) this.emit("onScan", barcode.data);
  }

  private async connectView(element: HTMLElement) {
    if (!this.#initialized) return;

    // To visualize the ongoing barcode capturing process on screen, set up a data capture view that renders the
    // camera preview. The view must be connected to the data capture context.
    const view = await DataCaptureView.forContext(this.#context);
    // Connect the data capture view to the HTML element.
    view.connectToElement(element);

    // Add a control to be able to switch cameras.
    view.addControl(new CameraSwitchControl());

    // Add a barcode capture overlay to the data capture view to render the location of captured barcodes on top of
    // the video preview. This is optional, but recommended for better visual feedback.
    const barcodeCaptureOverlay: BarcodeCaptureOverlay =
      await BarcodeCaptureOverlay.withBarcodeCaptureForView(this.#barcodeCapture!, view);
    const viewfinder: Viewfinder = new LaserlineViewfinder();
    barcodeCaptureOverlay.setViewfinder(viewfinder);
  }

  turnOff() {
    this.#camera?.switchToDesiredState(FrameSourceState.Off);
    this.#barcodeCapture?.setEnabled(false);
  }

  standby() {
    this.#camera?.switchToDesiredState(FrameSourceState.Standby);
    this.#barcodeCapture?.setEnabled(false);
  }

  async turnOn(element: HTMLElement) {
    await this.connectView(element);
    await this.#context!.setFrameSource(this.#camera);
    // Switch the camera on to start streaming frames.
    this.#camera?.switchToDesiredState(FrameSourceState.On).then(() => {
      this.#barcodeCapture?.setEnabled(true);
    });
  }
}
const barcodeScanner = new BarcodeScanner();
export default barcodeScanner;
